/* eslint-disable @typescript-eslint/no-unsafe-argument */

export type TemplateContext = {
  [key: string]: any
}

export class TemplateEngine {
  private context: TemplateContext

  constructor(context: TemplateContext) {
    this.context = context
  }

  /**
   * テンプレート構文:
   * {{ variable }} - 変数の出力
   * {{ if condition }} ... {{ end }} - 条件分岐 ああああああア
   * {{ if condition }} ... {{ else }} ... {{ end }} - else付き条件分岐
   * {{ for item in items }} ... {{ end }} - ループ
   */
  render(template: string): string {
    let result = template

    // 1. for ループの処理（ネストに対応するため再帰的に）
    result = this.processForLoops(result)

    // 2. if 文の処理（ネストに対応）
    result = this.processConditionals(result)

    // 3. 変数の置換
    result = this.processVariables(result)

    return result
  }

  private processVariables(template: string): string {
    return template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (match, expression) => {
      const trimmed = expression.trim()

      // 制御文はスキップ
      if (this.isControlStatement(trimmed)) {
        return match
      }

      try {
        const value = this.evaluateExpression(trimmed)
        return this.formatValue(value)
      } catch (e) {
        console.error(`Template variable error: ${expression}`, e)
        return ''
      }
    })
  }

  private processConditionals(template: string): string {
    // {{ if condition }} ... {{ else }} ... {{ end }} の処理
    const ifElseRegex =
      /\{\{\s*if\s+(.+?)\s*\}\}([\s\S]*?)\{\{\s*else\s*\}\}([\s\S]*?)\{\{\s*end\s*\}\}/g

    let result = template.replace(
      ifElseRegex,
      (match, condition, trueContent, falseContent) => {
        try {
          const conditionResult = this.evaluateExpression(condition.trim())
          return conditionResult ? trueContent : falseContent
        } catch (e) {
          console.error(`Template conditional error: ${condition}`, e)
          return ''
        }
      },
    )

    // {{ if condition }} ... {{ end }} の処理（elseなし）
    const ifRegex = /\{\{\s*if\s+(.+?)\s*\}\}([\s\S]*?)\{\{\s*end\s*\}\}/g

    result = result.replace(ifRegex, (match, condition, content) => {
      try {
        const conditionResult = this.evaluateExpression(condition.trim())
        return conditionResult ? content : ''
      } catch (e) {
        console.error(`Template conditional error: ${condition}`, e)
        return ''
      }
    })

    return result
  }

  private processForLoops(template: string): string {
    const forRegex =
      /\{\{\s*for\s+(\w+)(?:\s*,\s*(\w+))?\s+in\s+(.+?)\s*\}\}([\s\S]*?)\{\{\s*end\s*\}\}/g

    return template.replace(
      forRegex,
      (match, itemName, indexName, arrayExpression, content) => {
        try {
          const array = this.evaluateExpression(arrayExpression.trim())

          if (!Array.isArray(array)) {
            console.warn(
              `Expected array but got ${typeof array} for: ${arrayExpression}`,
            )
            return ''
          }

          return array
            .map((item, index) => {
              const loopContext: TemplateContext = {
                ...this.context,
                [itemName]: item,
              }

              // インデックス変数が指定されている場合
              if (indexName) {
                loopContext[indexName] = index
              }

              // デフォルトのインデックス変数
              loopContext[`${itemName}_index`] = index
              loopContext[`${itemName}_first`] = index === 0
              loopContext[`${itemName}_last`] = index === array.length - 1

              const loopEngine = new TemplateEngine(loopContext)
              return loopEngine.render(content)
            })
            .join('')
        } catch (e) {
          console.error(`Template loop error: ${arrayExpression}`, e)
          return ''
        }
      },
    )
  }

  private evaluateExpression(expression: string): any {
    try {
      // 論理演算子（&&, ||）- 短絡評価が必要なので先に処理
      if (expression.includes('||')) {
        const parts = expression.split('||').map((s) => s.trim())
        for (const part of parts) {
          const result = this.evaluateExpression(part)
          if (result) return true
        }
        return false
      }

      if (expression.includes('&&')) {
        const parts = expression.split('&&').map((s) => s.trim())
        for (const part of parts) {
          const result = this.evaluateExpression(part)
          if (!result) return false
        }
        return true
      }

      // 否定演算子
      if (expression.startsWith('!')) {
        return !this.evaluateExpression(expression.slice(1).trim())
      }

      // 比較演算子
      const operators = [
        { op: '===', fn: (a: any, b: any) => a === b },
        { op: '!==', fn: (a: any, b: any) => a !== b },
        { op: '==', fn: (a: any, b: any) => a == b },
        { op: '!=', fn: (a: any, b: any) => a != b },
        { op: '>=', fn: (a: any, b: any) => a >= b },
        { op: '<=', fn: (a: any, b: any) => a <= b },
        { op: '>', fn: (a: any, b: any) => a > b },
        { op: '<', fn: (a: any, b: any) => a < b },
      ]

      for (const { op, fn } of operators) {
        if (expression.includes(op)) {
          const [left, right] = expression.split(op).map((s) => s.trim())
          const leftValue = this.getValue(left)
          const rightValue = this.parseValue(right)
          return fn(leftValue, rightValue)
        }
      }

      // 算術演算子
      if (expression.includes('+')) {
        const parts = expression.split('+').map((s) => s.trim())
        return parts.reduce((acc, part) => {
          const value = this.getValue(part)
          return (
            acc + (typeof value === 'number' ? value : parseFloat(value) || 0)
          )
        }, 0)
      }

      if (expression.includes('-')) {
        const parts = expression.split('-').map((s) => s.trim())
        const first = this.getValue(parts[0])
        return parts.slice(1).reduce(
          (acc, part) => {
            const value = this.getValue(part)
            return (
              acc - (typeof value === 'number' ? value : parseFloat(value) || 0)
            )
          },
          typeof first === 'number' ? first : parseFloat(first) || 0,
        )
      }

      if (expression.includes('*')) {
        const parts = expression.split('*').map((s) => s.trim())
        return parts.reduce((acc, part) => {
          const value = this.getValue(part)
          return (
            acc * (typeof value === 'number' ? value : parseFloat(value) || 1)
          )
        }, 1)
      }

      if (expression.includes('/')) {
        const parts = expression.split('/').map((s) => s.trim())
        const first = this.getValue(parts[0])
        return parts.slice(1).reduce(
          (acc, part) => {
            const value = this.getValue(part)
            return (
              acc / (typeof value === 'number' ? value : parseFloat(value) || 1)
            )
          },
          typeof first === 'number' ? first : parseFloat(first) || 1,
        )
      }

      // 単純な変数参照
      return this.getValue(expression)
    } catch (e) {
      console.error(`Expression evaluation error: ${expression}`, e)
      return undefined
    }
  }

  private getValue(path: string): any {
    // リテラル値の場合
    const literal = this.parseValue(path)
    if (literal !== path) {
      return literal
    }

    // パスを解析: article.title, items[0].name, user["email"] など
    const segments = this.parsePath(path)
    let current: any = this.context

    for (const segment of segments) {
      if (current === null || current === undefined) {
        return undefined
      }

      current = current[segment]
    }

    return current
  }

  private parsePath(path: string): string[] {
    const segments: string[] = []
    let current = ''
    let inBracket = false
    let bracketContent = ''

    for (let i = 0; i < path.length; i++) {
      const char = path[i]

      if (char === '[') {
        if (current) {
          segments.push(current)
          current = ''
        }
        inBracket = true
        bracketContent = ''
      } else if (char === ']' && inBracket) {
        // ブラケット内の値を評価
        const value = this.parseValue(bracketContent.trim())
        segments.push(String(value))
        inBracket = false
        bracketContent = ''
      } else if (char === '.' && !inBracket) {
        if (current) {
          segments.push(current)
          current = ''
        }
      } else {
        if (inBracket) {
          bracketContent += char
        } else {
          current += char
        }
      }
    }

    if (current) {
      segments.push(current)
    }

    return segments
  }

  private parseValue(value: string): any {
    const trimmed = value.trim()

    // 文字列リテラル（ダブルクォート）
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      return trimmed.slice(1, -1)
    }

    // 文字列リテラル（シングルクォート）
    if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
      return trimmed.slice(1, -1)
    }

    // 数値
    if (/^-?\d+\.?\d*$/.test(trimmed)) {
      return Number(trimmed)
    }

    // 真偽値
    if (trimmed === 'true') return true
    if (trimmed === 'false') return false
    if (trimmed === 'null') return null
    if (trimmed === 'undefined') return undefined

    // それ以外は変数として扱う
    return value
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined) {
      return ''
    }

    if (typeof value === 'object') {
      // オブジェクトや配列はJSON文字列として出力
      try {
        return JSON.stringify(value)
      } catch {
        return String(value)
      }
    }

    return String(value)
  }

  private isControlStatement(expression: string): boolean {
    const keywords = ['if', 'else', 'end', 'for', 'in']
    const firstWord = expression.split(/\s+/)[0]
    return keywords.includes(firstWord)
  }
}
